import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckIcon, XIcon, Loader2Icon, ChevronLeftIcon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import type { QoEFinancialStatement } from '@/components/qoe/QoEFinancialStatementCard'

const fmtNum = (v: unknown): string => {
  if (v == null || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (isNaN(n)) return String(v)
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(n)
}

export function QoEFSApprovalPage() {
  const { fsId } = useParams<{ fsId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const role = (user as { role?: string } | null)?.role ?? ''
  const canApprove = role === 'cpa' || role === 'super_admin'

  const [fs, setFs] = useState<QoEFinancialStatement | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)

  useEffect(() => {
    if (!fsId) return
    apiClient
      .get<QoEFinancialStatement>(`/v1/qoe/financial-statements/${fsId}`)
      .then(r => setFs(r.data))
      .catch(() => toast.error('Failed to load financial statement'))
      .finally(() => setLoading(false))
  }, [fsId])

  async function handleApprove() {
    if (!fsId) return
    setActionLoading(true)
    try {
      await apiClient.post(`/v1/qoe/financial-statements/${fsId}/approve`)
      toast.success('Financial statement approved')
      navigate(-1)
    } catch {
      toast.error('Failed to approve')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleReject() {
    if (!fsId || !rejectReason.trim()) {
      toast.error('Please enter a rejection reason')
      return
    }
    setActionLoading(true)
    try {
      await apiClient.post(`/v1/qoe/financial-statements/${fsId}/reject`, { reason: rejectReason.trim() })
      toast.success('Financial statement sent back to draft')
      navigate(-1)
    } catch {
      toast.error('Failed to reject')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!fs) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Financial statement not found.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    )
  }

  const periods: Record<string, Record<string, unknown>> =
    (fs.parsed_data as { periods?: Record<string, Record<string, unknown>> } | null)?.periods ?? {}

  const periodKeys = Object.keys(periods)
  const lineItems = periodKeys.length > 0
    ? Object.keys(periods[periodKeys[0]])
    : []

  const statusVariant =
    fs.status === 'approved' ? 'default'
    : fs.status === 'pending_review' ? 'outline'
    : 'secondary'

  return (
    <div className="p-4 lg:p-6 flex flex-col gap-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1">
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="font-heading text-lg font-semibold text-brand-text">
            CPA Review — {fs.period_label}
          </h1>
          <p className="text-xs text-muted-foreground">
            v{fs.version} · {fs.fs_type} · {fs.source_filename ?? 'Unknown file'}
          </p>
        </div>
        <Badge variant={statusVariant} className="ml-auto capitalize">
          {fs.status.replace('_', ' ')}
        </Badge>
      </div>

      {/* Parsed data table */}
      {periodKeys.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-64">Line Item</TableHead>
                {periodKeys.map(p => (
                  <TableHead key={p} className="text-right">{p}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map(item => (
                <TableRow key={item}>
                  <TableCell className="font-medium text-sm">{item}</TableCell>
                  {periodKeys.map(p => (
                    <TableCell key={p} className="text-right tabular-nums text-sm">
                      {fmtNum(periods[p]?.[item])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
          No parsed financial data available for review.
        </div>
      )}

      {/* Rejection reason (if any) */}
      {fs.rejection_reason && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <span className="font-medium">Previous rejection reason: </span>
          {fs.rejection_reason}
        </div>
      )}

      {/* Actions (CPA / super_admin only, pending_review only) */}
      {canApprove && fs.status === 'pending_review' && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <Button
              onClick={handleApprove}
              disabled={actionLoading}
              className="gap-1.5"
            >
              {actionLoading ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
              Approve
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowRejectForm(v => !v)}
              disabled={actionLoading}
              className="gap-1.5"
            >
              <XIcon className="h-4 w-4" />
              Reject
            </Button>
          </div>

          {showRejectForm && (
            <div className="flex flex-col gap-2">
              <Textarea
                placeholder="Enter rejection reason (required)"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                className="text-sm"
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="self-start gap-1.5"
              >
                {actionLoading && <Loader2Icon className="h-3.5 w-3.5 animate-spin" />}
                Confirm Rejection
              </Button>
            </div>
          )}
        </div>
      )}

      {fs.status === 'approved' && (
        <p className="text-sm text-emerald-600 font-medium">
          Approved by {fs.approved_by ?? 'CPA'}
          {fs.approved_at ? ` on ${new Date(fs.approved_at).toLocaleDateString()}` : ''}
        </p>
      )}
    </div>
  )
}
